// src/features/riskmize/IncidentDetailPanel.jsx
//
// Detail pane of the IncidentsDrawer. Reads state.riskmize.selectedIncidentId,
// fetches GET /api/riskmize/incidents/:id on mount/change, renders the full
// incident card. Canonical field names: `source_link` and `created_at` — do
// NOT reintroduce `source_url` / `reported_at` (dead names from early specs).
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Clock, ExternalLink, Copy, Check } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import { riskmizeActions } from '@/store';
import { riskmizeApi } from './riskmize.api';
import { SEVERITY_COLORS } from './severity';
import { formatCoordinate, COORDINATE_FORMATS } from '@/lib/coordinates';

dayjs.extend(relativeTimePlugin);

const STATUS_COLORS = {
  active:   'text-rose-700  bg-rose-100    dark:bg-rose-900/30',
  resolved: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30',
  archived: 'text-slate-700 bg-slate-100   dark:bg-slate-800',
};

export default function IncidentDetailPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedId = useSelector((s) => s.riskmize.selectedIncidentId);

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coordFormat, setCoordFormat] = useState('decimal');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!selectedId) { setIncident(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    riskmizeApi.getIncident(selectedId).then((data) => {
      if (!cancelled) { setIncident(data); setLoading(false); }
    }).catch((err) => {
      if (!cancelled) { setError(String(err?.message ?? err)); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  const handleCopyCoords = async () => {
    if (!incident) return;
    const text = formatCoordinate(incident.latitude, incident.longitude, coordFormat);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  const severity = incident?.severity || 'low';
  const severityColor = SEVERITY_COLORS[severity] || SEVERITY_COLORS.low;
  const status = incident?.status || 'active';
  const statusClass = STATUS_COLORS[status] || STATUS_COLORS.active;

  return (
    <div className="flex flex-col h-full">
      {/* Back button */}
      <div className="px-3 py-2 border-b border-slate-200/80 dark:border-slate-700/60">
        <button
          type="button"
          onClick={() => dispatch(riskmizeActions.returnToList())}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
        >
          <ArrowLeft size={14} />
          {t('riskmize.back', 'Back to list')}
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-xs text-slate-500">
          <div className="w-5 h-5 border-2 border-slate-200 border-t-rose-500 rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="m-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-xs text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {!loading && !error && incident && (
        <div className="flex-1 overflow-y-auto">
          {/* Hero */}
          <div className="relative h-40 w-full">
            {incident.image_url ? (
              <img
                src={incident.image_url}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="h-full w-full" style={{ background: severityColor }} />
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* Title */}
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-snug">
              {incident.title || t('riskmize.untitledIncident', 'Untitled incident')}
            </h2>

            {/* Badges */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-md"
                style={{ background: severityColor }}
              >
                {severity}
              </span>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${statusClass}`}>
                {status}
              </span>
              {incident.category_name && (
                <span className="text-[10px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                  {incident.category_name}
                </span>
              )}
            </div>

            {/* Description */}
            {incident.description && (
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                {incident.description}
              </p>
            )}

            {/* Location */}
            {(incident.province || incident.country) && (
              <div className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span>{[incident.province, incident.country].filter(Boolean).join(', ')}</span>
              </div>
            )}

            {/* Incident date (uses incident_date; falls back to created_at) */}
            {(incident.incident_date || incident.created_at) && (
              <div className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                <Clock size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div>{dayjs(incident.incident_date || incident.created_at).format('YYYY-MM-DD HH:mm')}</div>
                  <div className="text-[11px] text-slate-400">
                    {dayjs(incident.incident_date || incident.created_at).fromNow()}
                  </div>
                </div>
              </div>
            )}

            {/* Resolved */}
            {incident.resolved_at && (
              <div className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                <Check size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('riskmize.resolvedAt', 'Resolved')}: {dayjs(incident.resolved_at).format('YYYY-MM-DD HH:mm')}
                </span>
              </div>
            )}

            {/* Coordinates + format toggle + copy */}
            {(incident.latitude != null && incident.longitude != null) && (
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t('riskmize.coordinates', 'Coordinates')}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {COORDINATE_FORMATS.map((fmt) => (
                    <button
                      key={fmt.value}
                      type="button"
                      onClick={() => setCoordFormat(fmt.value)}
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-md transition-colors ${
                        coordFormat === fmt.value
                          ? 'bg-rose-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                      }`}
                    >
                      {t(fmt.label, fmt.value)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-md truncate">
                    <bdi>{formatCoordinate(incident.latitude, incident.longitude, coordFormat)}</bdi>
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyCoords}
                    title={t('common.copy', 'Copy')}
                    className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                  >
                    {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
            )}

            {/* External source link */}
            {incident.source_link && (
              <a
                href={incident.source_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 dark:text-rose-400 hover:underline"
              >
                <ExternalLink size={12} />
                {t('riskmize.openSource', 'Open source article')}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
